import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { IcArrowLeft, IcPhone, IcPrinter, IcTrash, IcBan, IcCheck, IcSend, IcFileText, IcSave, IcClose, IcShoppingCart, IcAlertTriangle, IcTruck, IcLink, IcPackageOpen } from '../../theme/icons';
import Swal from '../../theme/swal';
import { format } from 'date-fns';
import commandeService from '../../services/commandeService';
import LinkFacturePopup from './LinkFacturePopup';
import supplierService from '../../services/supplierService';
import productService from '../../services/productService';
import categoryService from '../../services/categoryService';
import subcategoryService from '../../services/subcategoryService';
import laboratoireService from '../../services/laboratoireService';
import reservationService from '../../services/reservationService';
import {
  normProductName, duplicateProductAlert, extractApiErrorMessage, isDuplicateProductNameError,
} from '../../utils/duplicateProduct';
import { useCompany } from '../../context/CompanyContext';
import { StatusBadge } from './CommandesList';
import KI from '../../theme/palette';
import { filterSearch } from '../../utils/search';

const fmt = (v) => `${(parseFloat(v) || 0).toFixed(3)} DT`;

const CommandeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Carte « créer le nouveau produit reçu » : drafts string liés
  // (achat TTC ⇄ marge ⇄ vente). La TVA/marge par défaut viennent des
  // réglages société (19 % / 50 % en secours).
  const [createPop, setCreatePop] = useState(null);
  // Popup « Changer de fournisseur » (brouillon) : {suppliers, query}.
  const [supPop, setSupPop] = useState(null);
  // Popup « Lier à une facture existante » (envoyé) — composant partagé avec
  // la liste des bons envoyés. Cas du vendeur qui a saisi la facture
  // directement (sans passer par la réception du bon).
  const [linkPopOpen, setLinkPopOpen] = useState(false);
  const { tva: companyTva, marge: companyMarge } = useCompany();
  // Listes de référence de la fiche produit (chargées à l'ouverture de la carte).
  const [refs, setRefs] = useState({ loaded: false, cats: [], subs: [], labs: [] });

  useEffect(() => {
    if (!createPop || refs.loaded) return;
    Promise.all([
      categoryService.getAllCategories().catch(() => []),
      subcategoryService.getAllSubcategories().catch(() => []),
      laboratoireService.getAllLaboratoires().catch(() => []),
    ]).then(([c, s, l]) => {
      const arr = (x) => (Array.isArray(x) ? x : (x && x.results) || []);
      setRefs({ loaded: true, cats: arr(c), subs: arr(s), labs: arr(l) });
    });
  }, [createPop, refs.loaded]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await commandeService.getOrderById(id);
      setOrder(data);
      // Bon ENVOYE : la vérification repart de zéro à chaque ouverture /
      // refresh — on re-vérifie depuis le premier produit. Les états ne
      // deviennent définitifs (badges du bon reçu) qu'à la réception.
      setItems((data.items || []).map((i) => ({
        ...i,
        ...(data.status === 'ENVOYE' ? { check_status: '' } : {}),
      })));
      setDirty(false);
    } catch (e) {
      console.error('Erreur chargement bon de commande:', e);
      Swal.fire('Erreur', 'Bon de commande introuvable', 'error');
      navigate('/commandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = order && ['BROUILLON', 'ENVOYE'].includes(order.status);
  // Réception d'un bon ENVOYE : chaque ligne doit être vérifiée d'abord.
  const allVerified = items.length > 0 && items.every((i) => i.check_status);
  const total = items.reduce(
    (s, i) => s + (parseFloat(i.expected_cost) || 0) * (parseInt(i.quantity) || 0), 0);

  const updateItem = (itemId, patch) => {
    setItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, ...patch } : i)));
    setDirty(true);
  };

  const removeItem = (itemId) => {
    setItems((prev) => prev.filter((i) => i.id !== itemId));
    setDirty(true);
  };

  // Changement de fournisseur — possible uniquement tant que le bon est
  // brouillon (le backend refuse au-delà). Même barre de recherche que le
  // champ fournisseur du panier « Nouvelle commande ».
  const changeSupplier = async () => {
    try {
      const data = await supplierService.getAllSuppliers();
      setSupPop({ suppliers: Array.isArray(data) ? data : [], query: '' });
    } catch (e) {
      Swal.fire('Erreur', 'Impossible de charger la liste des fournisseurs', 'error');
    }
  };

  const applySupplier = async (supplierId) => {
    setSupPop(null);
    setBusy(true);
    try {
      await commandeService.updateOrder(order.id, { supplier: supplierId });
      await load();
    } catch (e) {
      Swal.fire('Erreur',
        e.response?.data?.detail || 'Échec du changement de fournisseur', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Vérification par ligne, persistée immédiatement à chaque clic.
  const setCheck = async (itemId, value) => {
    const line = items.find((i) => i.id === itemId);
    let newValue = value;
    if (order.status === 'ENVOYE') {
      // Boutons NON togglables sur un bon envoyé : re-cliquer l'état actif ne
      // décoche rien (c'est ce qui faisait « perdre » les ✓). Sur un nouveau
      // produit reçu sans fiche, re-cliquer ✓ rouvre la carte de création.
      if (line && line.check_status === value) {
        if (value === 'RECU' && !line.product) openCreatePop(line);
        return;
      }
    } else {
      // Brouillon : un seul bouton (Manque) — le re-clic le retire.
      newValue = line && line.check_status === value ? '' : value;
    }
    const next = items.map((i) => (i.id === itemId
      ? { ...i, check_status: newValue }
      : i));
    setItems(next);
    try {
      const data = await commandeService.updateOrder(order.id, { items: itemsPayload(next) });
      setItems((data.items || []).map((i) => ({ ...i })));
    } catch (e) {
      console.error('Erreur vérification:', e);
      Swal.fire('Erreur', 'Échec de l\'enregistrement de la vérification', 'error');
      return;
    }
    // ✓ reçu sur un NOUVEAU produit (pas encore dans la liste des produits) →
    // carte de création sur place : le prix estimé est un prix d'achat TTC,
    // la carte calcule le HT (fiche) et le prix de vente à la marge société.
    if (newValue === 'RECU' && line && !line.product) {
      openCreatePop(line);
    }
  };

  // Ouvre la carte de création pour une ligne « nouveau produit ». La ligne
  // est identifiée par son NOM (clé stable) : le backend recrée les lignes
  // avec de nouveaux ids à chaque enregistrement, un id serait périmé.
  const openCreatePop = (line) => {
    const achat = parseFloat(line.expected_cost) || 0;
    setCreatePop({
      lineName: line.product_name || '',
      name: line.product_name || '',
      code: '',
      description: '',
      category: '',
      subcategory: '',
      laboratoire: '',
      stockMin: '0',
      imageFile: null,
      imagePreview: null,
      achat: achat.toFixed(3),
      marge: String(companyMarge),
      vente: (achat * (1 + (companyMarge || 0) / 100)).toFixed(3),
    });
  };

  // « + Nouveau » sur le champ Laboratoire de la carte : crée le labo à la
  // volée puis le sélectionne dans la fiche.
  const addNewLabo = async () => {
    const { value: laboName } = await Swal.fire({
      title: 'Nouveau laboratoire',
      input: 'text',
      inputLabel: 'Nom du laboratoire',
      inputPlaceholder: 'Ex : Laboratoire XYZ',
      showCancelButton: true,
      confirmButtonText: 'Créer',
      cancelButtonText: 'Annuler',
      inputValidator: (v) => (!v || !v.trim() ? 'Entrez le nom du laboratoire.' : undefined),
    });
    if (!laboName) return;
    try {
      const created = await laboratoireService.createLaboratoire({ name: laboName.trim() });
      setRefs((r) => ({ ...r, labs: [...r.labs, created] }));
      setCreatePop((c) => (c ? { ...c, laboratoire: String(created.id) } : c));
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: `Laboratoire créé — ${created.name}`, timer: 2000, showConfirmButton: false,
      });
    } catch (e) {
      console.error('Erreur création laboratoire:', e);
      // Message de champ DRF (ex: « Un laboratoire avec ce nom existe déjà. »).
      let message = e && e.message;
      if (!message && e && typeof e === 'object') {
        const first = Object.values(e)[0];
        message = Array.isArray(first) ? first[0] : first;
      }
      Swal.fire('Erreur', message || 'Échec de la création du laboratoire', 'error');
    }
  };

  // Liaison achat TTC ⇄ marge ⇄ vente de la carte création.
  const parseNum = (v) => parseFloat(String(v).replace(',', '.'));
  const setCreateAchat = (value) => setCreatePop((prev) => {
    const achat = parseNum(value);
    const marge = parseNum(prev.marge);
    const vente = (Number.isFinite(achat) && Number.isFinite(marge))
      ? (achat * (1 + marge / 100)).toFixed(3)
      : prev.vente;
    return { ...prev, achat: value, vente };
  });
  const setCreateMarge = (value) => setCreatePop((prev) => {
    const achat = parseNum(prev.achat);
    const marge = parseNum(value);
    const vente = (Number.isFinite(achat) && Number.isFinite(marge))
      ? (achat * (1 + marge / 100)).toFixed(3)
      : prev.vente;
    return { ...prev, marge: value, vente };
  });
  const setCreateVente = (value) => setCreatePop((prev) => {
    const achat = parseNum(prev.achat);
    const vente = parseNum(value);
    const marge = (Number.isFinite(achat) && achat > 0 && Number.isFinite(vente))
      ? (((vente - achat) / achat) * 100).toFixed(1)
      : prev.marge;
    return { ...prev, vente: value, marge };
  });

  // Crée le produit (fiche : coût HT = TTC / (1 + TVA société)) puis relie la
  // ligne du bon au nouveau produit — il entrera ainsi dans la facture.
  const saveCreate = async () => {
    if (!createPop || createPop.saving) return;
    const achatTTC = parseNum(createPop.achat);
    const vente = parseNum(createPop.vente);
    const name = (createPop.name || '').trim();
    if (!name || !Number.isFinite(achatTTC) || achatTTC < 0) return;
    const ht = achatTTC / (1 + (companyTva || 0) / 100);
    setCreatePop((c) => ({ ...c, saving: true }));
    try {
      let created;
      if (createPop.imageFile) {
        // Multipart (photo choisie) — mêmes champs que la page Ajouter un produit.
        const fd = new FormData();
        fd.append('name', name);
        fd.append('code', createPop.code || '');
        fd.append('description', createPop.description || '');
        fd.append('cost', +ht.toFixed(3));
        fd.append('price', Number.isFinite(vente) && vente >= 0 ? +vente.toFixed(3) : 0);
        fd.append('remise', 0);
        fd.append('stock', 0);
        fd.append('stock_min', parseInt(createPop.stockMin, 10) || 0);
        if (createPop.category) fd.append('category', createPop.category);
        if (createPop.subcategory) fd.append('subcategory', createPop.subcategory);
        if (createPop.laboratoire) fd.append('laboratoire', createPop.laboratoire);
        fd.append('image', createPop.imageFile);
        created = await productService.createProductForm(fd);
      } else {
        created = await productService.createProduct({
          name,
          code: createPop.code || null,
          description: createPop.description || null,
          cost: +ht.toFixed(3),
          price: Number.isFinite(vente) && vente >= 0 ? +vente.toFixed(3) : 0,
          remise: 0,
          stock: 0,
          stock_min: parseInt(createPop.stockMin, 10) || 0,
          category: createPop.category || null,
          subcategory: createPop.subcategory || null,
          laboratoire: createPop.laboratoire || null,
        });
      }
      // Relier la ligne du bon au produit créé (clé = nom d'origine, les ids
      // de lignes changent à chaque enregistrement). Le stock viendra de la
      // facture.
      const next = items.map((i) => (!i.product && (i.product_name || '') === createPop.lineName
        ? { ...i, product: created.id, product_name: name }
        : i));
      const data = await commandeService.updateOrder(order.id, { items: itemsPayload(next) });
      setItems((data.items || []).map((i) => ({ ...i })));
      // Relier aussi la (les) réservation(s) « nouveau produit » du même nom :
      // la carte violette disparaît de Nouvelle commande (plus de doublon) et
      // la réservation suit désormais le produit créé.
      try {
        const res = await reservationService.getReservations({ status: 'pending' });
        const list = Array.isArray(res) ? res : (res && res.results) || [];
        const key = (createPop.lineName || '').trim().toLowerCase();
        const matches = list.filter((r) => !r.product
          && (r.product_name || '').trim().toLowerCase() === key);
        await Promise.all(matches.map((r) => reservationService.updateReservation(r.id, { product: created.id })));
      } catch (linkErr) {
        console.error('Liaison réservation → produit:', linkErr);
      }
      setCreatePop(null);
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: `Produit créé — ${name}`, timer: 2200, showConfirmButton: false,
      });
    } catch (e) {
      console.error('Erreur création produit:', e);
      setCreatePop((c) => (c ? { ...c, saving: false } : c));
      const message = extractApiErrorMessage(e);
      // Nom en doublon → carte « Produit déjà au catalogue » (brand kit) avec
      // la fiche existante, et option de relier la ligne du bon à cette fiche
      // au lieu d'en créer une nouvelle.
      if (isDuplicateProductNameError(message)) {
        let existing = null;
        try {
          const all = await productService.getAllProducts();
          const list = all.results || all || [];
          existing = list.find((p) => normProductName(p.name) === normProductName(name)) || null;
        } catch (fetchErr) {
          console.error('Recherche de la fiche existante:', fetchErr);
        }
        const r = await duplicateProductAlert({
          product: existing, name,
          confirmText: existing ? 'Utiliser la fiche existante' : undefined,
        });
        if (r.isConfirmed && existing) {
          try {
            // Même liaison que pour une fiche fraîchement créée : la ligne du
            // bon (et les réservations du même nom) suivent la fiche existante.
            const next = items.map((i) => (!i.product && (i.product_name || '') === createPop.lineName
              ? { ...i, product: existing.id, product_name: existing.name }
              : i));
            const data = await commandeService.updateOrder(order.id, { items: itemsPayload(next) });
            setItems((data.items || []).map((i) => ({ ...i })));
            try {
              const res = await reservationService.getReservations({ status: 'pending' });
              const pending = Array.isArray(res) ? res : (res && res.results) || [];
              const key = (createPop.lineName || '').trim().toLowerCase();
              const matches = pending.filter((rr) => !rr.product
                && (rr.product_name || '').trim().toLowerCase() === key);
              await Promise.all(matches.map((rr) => reservationService.updateReservation(rr.id, { product: existing.id })));
            } catch (linkErr) {
              console.error('Liaison réservation → produit:', linkErr);
            }
            setCreatePop(null);
            Swal.fire({
              toast: true, position: 'top-end', icon: 'success',
              title: `Fiche existante reliée — ${existing.name}`, timer: 2200, showConfirmButton: false,
            });
          } catch (linkErr) {
            console.error('Liaison ligne → fiche existante:', linkErr);
            Swal.fire('Erreur', 'Échec de la liaison à la fiche existante', 'error');
          }
        }
        return;
      }
      Swal.fire('Erreur', message || 'Échec de la création du produit', 'error');
    }
  };

  const itemsPayload = (list) => list.map((i) => ({
    product: i.product,
    // Nom obligatoire pour une ligne « nouveau produit » (sans fiche) — sans
    // lui, le backend écarterait la ligne au remplacement des items.
    product_name: i.product ? undefined : (i.product_name || ''),
    quantity: parseInt(i.quantity) || 1,
    free_quantity: parseInt(i.free_quantity) || 0,
    expected_cost: parseFloat(i.expected_cost) || 0,
    check_status: i.check_status || '',
  }));

  // Persiste les lignes telles quelles (utilisé par le bouton Enregistrer et
  // automatiquement avant « Commande passée » / « Réception »).
  const persistItems = async () => {
    await commandeService.updateOrder(order.id, { items: itemsPayload(items) });
    setDirty(false);
  };

  const saveItems = async () => {
    if (items.length === 0) {
      Swal.fire('Impossible', 'Un bon de commande doit garder au moins un produit. Supprimez ou annulez le bon à la place.', 'info');
      return;
    }
    setBusy(true);
    try {
      await persistItems();
      await load();
      Swal.fire({ title: 'Enregistré', icon: 'success', timer: 1200, showConfirmButton: false });
    } catch (e) {
      console.error('Erreur enregistrement:', e);
      Swal.fire('Erreur', 'Échec de l\'enregistrement des modifications', 'error');
    } finally {
      setBusy(false);
    }
  };

  const doMarkSent = async () => {
    const c = await Swal.fire({
      title: 'Commande passée ?',
      text: 'Marquer ce bon comme envoyé (commande passée au fournisseur).',
      icon: 'question',
      input: 'date',
      inputLabel: 'Arrivée prévue (facultatif)',
      showCancelButton: true,
      confirmButtonText: 'Oui, envoyé',
      cancelButtonText: 'Pas encore',
      confirmButtonColor: KI.ramps.blue[600],
    });
    if (!c.isConfirmed) return;
    setBusy(true);
    try {
      // Les modifications en cours sont enregistrées automatiquement.
      if (dirty) await persistItems();
      await commandeService.markSent(
        order.id,
        c.value ? { expected_arrival_date: c.value } : {},
      );
      navigate('/commandes/envoyes');
    } catch (e) {
      Swal.fire('Erreur', 'Échec du changement de statut', 'error');
      setBusy(false);
    }
  };

  const doCancel = async () => {
    const c = await Swal.fire({
      title: 'Annuler ce bon de commande ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, annuler',
      cancelButtonText: 'Non',
      confirmButtonColor: KI.coralDark,
    });
    if (!c.isConfirmed) return;
    setBusy(true);
    try {
      await commandeService.cancelOrder(order.id);
      await load();
    } catch (e) {
      Swal.fire('Erreur', 'Échec de l\'annulation', 'error');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    const c = await Swal.fire({
      title: 'Supprimer définitivement ?',
      text: `Le bon de commande BC #${order.number} sera supprimé.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.coralDark,
    });
    if (!c.isConfirmed) return;
    try {
      await commandeService.deleteOrder(order.id);
      navigate('/commandes');
    } catch (e) {
      Swal.fire('Erreur', 'Échec de la suppression', 'error');
    }
  };

  // « Reçu sans facture » : le fournisseur livre parfois un bon en 2 ou 3
  // factures — impossible de le relier à UNE facture. Le bon passe à RECU
  // sans facture liée ; les factures se saisissent via Ajouter Facturation.
  const doReceivedNoFacture = async () => {
    // Popup volontairement épurée (retour Ahmed 2026-08-20) : pas d'icône,
    // pas de libellé au-dessus du champ — juste la note (facultative).
    const c = await Swal.fire({
      title: 'Marquer reçu sans facture ?',
      html: `Le bon <b>BC #${order.number}</b> passera à <b>Reçu</b> sans facture liée.`,
      input: 'text',
      inputPlaceholder: 'Note : n° de facture, remarque… (facultatif)',
      inputValue: order.received_note || '',
      showCancelButton: true,
      confirmButtonText: 'Oui, reçu sans facture',
      cancelButtonText: 'Annuler',
      confirmButtonColor: KI.spruce,
    });
    if (!c.isConfirmed) return;
    setBusy(true);
    try {
      await commandeService.markReceived(order.id, null, (c.value || '').trim());
      await load();
      Swal.fire({
        toast: true, position: 'top-end', icon: 'success',
        title: `BC #${order.number} marqué reçu (sans facture)`, timer: 2200, showConfirmButton: false,
      });
    } catch (e) {
      console.error('Erreur réception sans facture:', e);
      Swal.fire('Erreur',
        e.response?.data?.detail || 'Échec du changement de statut', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Reception: jump into the normal « Ajouter Facturation » flow, pre-filled.
  // The facture is what updates stock; on success AddFacturation links it back
  // and this bon passes to RECU.
  const doReception = async () => {
    // Une ligne « nouveau produit » non encore créée dans la liste ne peut
    // pas aller sur la facture (pas de fiche produit).
    const received = items.filter((i) => i.product
      && !['MANQUE', 'NON_RECU'].includes(i.check_status));
    const uncreated = items.filter((i) => !i.product && i.check_status === 'RECU');
    if (uncreated.length > 0) {
      const c = await Swal.fire({
        icon: 'info',
        title: 'Nouveau produit à créer d\'abord',
        text: `« ${uncreated[0].product_name} » est reçu mais pas encore créé dans la liste des produits — créez-le pour qu'il entre dans la facture.`,
        showCancelButton: true,
        confirmButtonText: 'Créer maintenant',
        cancelButtonText: received.length ? 'Continuer sans lui' : 'Annuler',
        confirmButtonColor: KI.ramps.blue[600],
      });
      if (c.isConfirmed) {
        openCreatePop(uncreated[0]);
        return;
      }
      if (received.length === 0) return;
    }
    if (received.length === 0) {
      Swal.fire('Rien à facturer',
        'Tous les produits sont marqués « Manque » ou « Non reçu » — aucune ligne à mettre sur la facture.',
        'info');
      return;
    }
    // Les modifications en cours sont enregistrées automatiquement.
    if (dirty) {
      try {
        await persistItems();
      } catch (e) {
        console.error('Erreur enregistrement avant réception:', e);
        Swal.fire('Erreur', 'Échec de l\'enregistrement des modifications', 'error');
        return;
      }
    }
    navigate('/facturation/add', {
      state: {
        commande: {
          id: order.id,
          number: order.number,
          supplier: {
            id: order.supplier,
            name: order.supplier_name,
            phone: order.supplier_phone,
          },
          // Les lignes marquées « Manque » ou « Non reçu » à la vérification
          // n'entrent pas dans la facture.
          items: received.map((i) => ({
            product: i.product,
            product_name: i.product_name,
            quantity: parseInt(i.quantity) || 1,
            free_quantity: parseInt(i.free_quantity) || 0,
            expected_cost: parseFloat(i.expected_cost) || 0,
          })),
        },
      },
    });
  };

  // Printable order sheet in its own window — keeps the app layout out of print.
  const doPrint = () => {
    const rows = items.map((i, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>${i.product_name || ''}</td>
        <td>${i.product_code || ''}</td>
        <td class="num">${i.quantity}${(parseInt(i.free_quantity) || 0) > 0 ? ` (+${i.free_quantity} gratuit)` : ''}</td>
        <td class="num">${(parseFloat(i.expected_cost) || 0).toFixed(3)}</td>
        <td class="num">${((parseFloat(i.expected_cost) || 0) * (parseInt(i.quantity) || 0)).toFixed(3)}</td>
      </tr>`).join('');
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><meta charset="utf-8">
      <title>Bon de commande BC #${order.number}</title>
      <style>
        body { font-family: Arial, sans-serif; color: ${KI.ink}; margin: 32px; }
        h1 { font-size: 20px; margin-bottom: 4px; }
        .meta { color: ${KI.ramps.gray[700]}; font-size: 13px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th, td { border: 1px solid ${KI.border}; padding: 6px 8px; text-align: left; }
        th { background: ${KI.ramps.gray[100]}; }
        .num { text-align: right; font-variant-numeric: tabular-nums; }
        tfoot td { font-weight: bold; }
        .footer { margin-top: 24px; font-size: 12px; color: ${KI.muted}; }
      </style></head><body>
      <h1>Bon de commande — BC #${order.number}</h1>
      <div class="meta">
        Fournisseur : <b>${order.supplier_name}</b>
        ${order.supplier_phone ? ` — Tél : ${order.supplier_phone}` : ''}<br>
        Date : ${order.date_added ? format(new Date(order.date_added), 'dd/MM/yyyy HH:mm') : ''}
        — Statut : ${order.status}
      </div>
      <table>
        <thead><tr><th>#</th><th>Produit</th><th>Code</th><th class="num">Qté</th><th class="num">Prix unit. (DT)</th><th class="num">Total (DT)</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><td colspan="5">Total estimé</td><td class="num">${total.toFixed(3)}</td></tr></tfoot>
      </table>
      <div class="footer">Prix estimés d'après les derniers achats — les prix réels seront saisis sur la facture à la réception.</div>
      <script>window.onload = () => window.print();</` + `script>
      </body></html>`);
    w.document.close();
  };

  if (loading || !order) {
    return <div className="p-10 text-center text-gray-500">Chargement…</div>;
  }

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(({
              BROUILLON: '/commandes/brouillons',
              ENVOYE: '/commandes/envoyes',
              RECU: '/commandes/recus',
            })[order.status] || '/commandes')}
            className="p-2.5 btn-s"
            title="Retour à la liste"
          >
            <IcArrowLeft />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
              BC #{order.number}
              <StatusBadge status={order.status} />
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Créé le {order.date_added ? format(new Date(order.date_added), 'dd/MM/yyyy HH:mm') : '—'}
              {order.date_sent && <> · Envoyé le {format(new Date(order.date_sent), 'dd/MM/yyyy HH:mm')}</>}
              {order.expected_arrival_date && <> · Arrivée prévue le {format(new Date(order.expected_arrival_date), 'dd/MM/yyyy')}</>}
              {order.date_received && <> · Reçu le {format(new Date(order.date_received), 'dd/MM/yyyy HH:mm')}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={doPrint}
            className="px-4 py-2.5 flex items-center btn-s"
          >
            <IcPrinter className="mr-2 text-sm" /> Imprimer
          </button>
          {order.status === 'BROUILLON' && (
            <button
              type="button"
              onClick={doDelete}
              title="Supprimer le bon de commande"
              aria-label="Supprimer le bon de commande"
              className="p-2.5 rounded-xl flex items-center justify-center text-red-600 hover:bg-red-50"
            >
              <IcTrash />
            </button>
          )}
        </div>
      </div>

      {/* Supplier card — copie du champ fournisseur du panier « Nouvelle
          commande » : chip (camion + nom + numéros en pastilles + croix) qui
          devient la barre de recherche au clic (brouillon uniquement). */}
      {/* Studio Ahmed 2026-08-17 : carte sans bordure, fond gray-100. */}
      <div className="bg-gray-100 rounded-2xl p-5 mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
        {supPop ? (
            /* Barre de recherche pleine largeur — elle remplace tout
               (nom + numéros), comme au panier. */
            <div className="relative">
              <IcTruck className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                autoFocus
                value={supPop.query}
                onChange={(e) => setSupPop((p) => ({ ...p, query: e.target.value }))}
                onBlur={() => setTimeout(() => setSupPop(null), 150)}
                onKeyDown={(e) => { if (e.key === 'Escape') setSupPop(null); }}
                placeholder="Écrire le nom du fournisseur…"
                className="w-full pl-11 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 bg-white"
              />
              {(() => {
                // Règle Ki Para (utils/search.js) : début de mot, « commence par » en tête.
                const alpha = supPop.suppliers
                  .filter((s) => s.id !== order.supplier)
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));
                const matches = filterSearch(alpha, supPop.query, { text: (s) => s.name }).slice(0, 8);
                return matches.length > 0 && (
                  <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden max-h-60 overflow-y-auto">
                    {matches.map((s) => (
                      <button
                        type="button"
                        key={s.id}
                        onMouseDown={(e) => { e.preventDefault(); applySupplier(s.id); }}
                        className="w-full px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 text-left border-b border-gray-50 last:border-b-0"
                      >
                        <span className="font-medium text-gray-800 truncate">{s.name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          {s.phone && (
                            <span className="text-xs text-gray-400 tabular-nums">{s.phone.split(',')[0].trim()}</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
        ) : (
          /* Chip fournisseur — même design que le panier : camion + nom +
             numéros en pastilles blanches + croix pour changer (brouillon). */
          <div
            onClick={order.status === 'BROUILLON' && !busy ? changeSupplier : undefined}
            role={order.status === 'BROUILLON' ? 'button' : undefined}
            title={order.status === 'BROUILLON' ? 'Changer de fournisseur (brouillon uniquement)' : undefined}
            className={`w-full pl-4 pr-3 py-2 border border-gray-900 bg-gray-50 rounded-xl flex items-center gap-3 flex-wrap min-h-[46px] ${
              order.status === 'BROUILLON' ? 'cursor-pointer' : ''
            }`}
          >
            <IcTruck className="text-gray-600 shrink-0" />
            <span className="font-semibold text-gray-900">{order.supplier_name}</span>
            {order.supplier_phone && order.supplier_phone.split(',').map((phone) => (
              <a
                key={phone}
                href={`tel:${phone.trim()}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 bg-white hover:bg-gray-100 text-gray-700 px-2.5 py-1 rounded-lg font-bold text-sm tabular-nums border border-gray-200"
                title="Appeler le fournisseur"
              >
                <IcPhone size={11} /> {phone.trim()}
              </a>
            ))}
            {order.status === 'BROUILLON' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!busy) changeSupplier(); }}
                className="ml-auto text-gray-400 hover:text-gray-700 p-1"
                title="Changer de fournisseur"
              >
                <IcClose />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Items */}
      <div className="bg-white shadow-sm border border-gray-100 rounded-2xl overflow-hidden mb-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100">
                <th className="px-4 py-3">Produit</th>
                <th className="px-4 py-3 text-center">Stock actuel</th>
                <th className="px-4 py-3 text-center">Qté</th>
                <th className="px-4 py-3 text-right">Prix unit. estimé</th>
                <th className="px-4 py-3 text-right">Total</th>
                {['BROUILLON', 'ENVOYE', 'RECU'].includes(order.status) && (
                  <th className="px-3 py-3 text-center">
                    Vérification
                    {order.status === 'ENVOYE' && (
                      <span className="bdg bdg-pos ml-1.5 tabular-nums normal-case">
                        {items.filter((i) => i.check_status).length}/{items.length}
                      </span>
                    )}
                  </th>
                )}
                {order.status === 'BROUILLON' && <th className="px-4 py-3 w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className={`border-b border-gray-50 ${order.status === 'ENVOYE' && !i.check_status ? 'bg-yellow-50/70' : ''}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-800">
                      {i.product_name || '(produit supprimé)'}
                      {!i.product && (
                        <span className="bdg bdg-new ml-2">Nouveau produit</span>
                      )}
                    </div>
                    {i.product_code && <div className="text-xs text-gray-400">{i.product_code}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-gray-600 tabular-nums">{i.product_stock ?? '—'}</td>
                  <td className="px-4 py-2.5 text-center">
                    {editable ? (
                      <div className="inline-flex items-center gap-1.5">
                        <input
                          type="number"
                          min="1"
                          value={i.quantity}
                          onChange={(e) => updateItem(i.id, { quantity: e.target.value })}
                          className="w-20 px-2 py-1.5 text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums"
                        />
                        <span className="text-xs text-green-700 font-semibold whitespace-nowrap">+</span>
                        <input
                          type="number"
                          min="0"
                          value={i.free_quantity ?? 0}
                          onChange={(e) => updateItem(i.id, { free_quantity: e.target.value })}
                          title="Unités gratuites offertes par le fournisseur"
                          className="w-16 px-2 py-1.5 text-center border border-green-200 bg-green-50/50 text-green-800 rounded-lg focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums"
                        />
                      </div>
                    ) : (
                      <span className="font-medium tabular-nums">
                        {i.quantity}
                        {(parseInt(i.free_quantity) || 0) > 0 && (
                          <span className="text-green-700 text-xs font-semibold ml-1">+{i.free_quantity} gratuit{i.free_quantity > 1 ? 's' : ''}</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {editable ? (
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={i.expected_cost}
                        onChange={(e) => updateItem(i.id, { expected_cost: e.target.value })}
                        className="w-24 px-2 py-1.5 text-right border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums"
                      />
                    ) : (
                      <span className="tabular-nums">{fmt(i.expected_cost)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums whitespace-nowrap">
                    {fmt((parseFloat(i.expected_cost) || 0) * (parseInt(i.quantity) || 0))}
                  </td>
                  {['BROUILLON', 'ENVOYE'].includes(order.status) && (
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <span className="inline-flex items-center gap-1.5">
                        {order.status === 'ENVOYE' && (
                          <button
                            type="button"
                            onClick={() => setCheck(i.id, 'RECU')}
                            title={i.check_status === 'RECU' ? 'Reçu — cliquer pour annuler' : 'Marquer reçu'}
                            className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-all ${
                              i.check_status === 'RECU'
                                ? 'bg-green-600 border-green-600 text-white shadow-sm'
                                : 'border-green-300 text-green-600 hover:bg-green-50'
                            }`}
                          >
                            <IcCheck size={12} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setCheck(i.id, 'MANQUE')}
                          title={i.check_status === 'MANQUE' ? 'Manque — cliquer pour annuler' : 'Marquer manquant'}
                          className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-all ${
                            i.check_status === 'MANQUE'
                              ? 'bg-amber-500 border-amber-500 text-white shadow-sm'
                              : 'border-amber-300 text-amber-600 hover:bg-amber-50'
                          }`}
                        >
                          <IcAlertTriangle size={12} />
                        </button>
                        {order.status === 'ENVOYE' && !i.product && i.check_status === 'RECU' && (
                          <button
                            type="button"
                            onClick={() => openCreatePop(i)}
                            title="Créer la fiche de ce nouveau produit"
                            className="px-2.5 h-8 text-[10px] btn-p transition-all"
                          >
                            Créer
                          </button>
                        )}
                        {order.status === 'ENVOYE' && (
                          <button
                            type="button"
                            onClick={() => setCheck(i.id, 'NON_RECU')}
                            title={i.check_status === 'NON_RECU' ? 'Non reçu — cliquer pour annuler' : 'Marquer non reçu'}
                            className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-all ${
                              i.check_status === 'NON_RECU'
                                ? 'bg-gray-600 border-gray-600 text-white shadow-sm'
                                : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <IcBan size={12} />
                          </button>
                        )}
                      </span>
                    </td>
                  )}
                  {order.status === 'RECU' && (
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      {i.check_status === 'RECU' && (
                        <span className="bdg bdg-pos">Reçu</span>
                      )}
                      {i.check_status === 'MANQUE' && (
                        <span className="bdg bdg-alert">Manque · {parseInt(i.quantity) || 0}</span>
                      )}
                      {i.check_status === 'NON_RECU' && (
                        <span className="bdg bdg-neutral">Non reçu</span>
                      )}
                      {!i.check_status && (
                        <span className="text-[11px] text-gray-300 font-medium">—</span>
                      )}
                    </td>
                  )}
                  {/* Retirer une ligne : brouillon uniquement — sur un bon
                      envoyé on marque Manque / Non reçu, on ne supprime pas. */}
                  {order.status === 'BROUILLON' && (
                    <td className="px-4 py-2.5 text-center">
                      <button
                        type="button"
                        onClick={() => removeItem(i.id)}
                        className="p-2 rounded-lg text-red-600 hover:bg-red-50"
                        title="Retirer ce produit"
                      >
                        <IcTrash size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50">
                <td colSpan={editable ? 4 : 4} className="px-4 py-3 text-right font-semibold text-gray-700">
                  Total estimé
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                  {fmt(total)}
                </td>
                {order.status === 'BROUILLON' && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {dirty && editable && (
        <div className="mb-5 flex justify-end">
          <button
            type="button"
            onClick={saveItems}
            disabled={busy}
            className="px-5 py-2.5 flex items-center btn-p"
          >
            <IcSave className="mr-2" /> Enregistrer les modifications
          </button>
        </div>
      )}

      {/* Status actions */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex gap-2">
          {order.status === 'BROUILLON' && (
            <button
              type="button"
              onClick={() => navigate('/commandes/a-commander', {
                state: {
                  continueOrder: {
                    id: order.id,
                    number: order.number,
                    supplier: order.supplier,
                    notes: order.notes || '',
                    items: items.map((i) => ({
                      product: i.product,
                      product_name: i.product_name,
                      product_stock: i.product_stock,
                      quantity: parseInt(i.quantity) || 1,
                      free_quantity: parseInt(i.free_quantity) || 0,
                      expected_cost: parseFloat(i.expected_cost) || 0,
                      check_status: i.check_status || '',
                    })),
                  },
                },
              })}
              disabled={busy}
              title="Retourner au catalogue pour ajouter des produits à ce bon"
              className="px-5 py-2.5 flex items-center btn-p"
            >
              <IcShoppingCart className="mr-2" /> Continuer la commande
            </button>
          )}
          {order.status === 'ENVOYE' && (
            <button
              type="button"
              onClick={doCancel}
              disabled={busy}
              className="px-4 py-2.5 flex items-center btn-g"
            >
              <IcBan className="mr-2 text-sm" /> Annuler le bon
            </button>
          )}
        </div>
        {/* Commande passée au-dessus, Réception juste en dessous */}
        <div className="flex flex-col items-stretch gap-2">
          {order.status === 'BROUILLON' && (
            <button
              type="button"
              onClick={doMarkSent}
              disabled={busy}
              title="Les modifications en cours seront enregistrées automatiquement"
              className="px-5 py-2.5 flex items-center justify-center btn-p"
            >
              <IcSend className="mr-2" /> Commande passée (envoyé)
            </button>
          )}
          {order.status === 'ENVOYE' && (
            <>
              <button
                type="button"
                onClick={doReception}
                disabled={busy || !allVerified}
                title={!allVerified
                  ? 'Vérifiez d\'abord tous les produits (reçu / manque / non reçu)'
                  : 'Marchandise arrivée : créer la facture pré-remplie (les modifications en cours sont enregistrées automatiquement)'}
                className="px-5 py-2.5 flex items-center justify-center btn-p"
              >
                <IcCheck className="mr-2" /> Réception → Créer la facture
              </button>
              {/* La facture a déjà été saisie (le vendeur a oublié de passer
                  par le bon) → relier le bon à cette facture existante. */}
              {/* Comme « Réception » : verrouillé tant que chaque ligne n'est
                  pas vérifiée (reçu / manque / non reçu). */}
              <button
                type="button"
                onClick={() => setLinkPopOpen(true)}
                disabled={busy || !allVerified}
                title={!allVerified
                  ? 'Vérifiez d\'abord tous les produits (reçu / manque / non reçu)'
                  : 'La facture a déjà été ajoutée ? Relier ce bon à une facture existante — il passera à Reçu'}
                className="px-5 py-2.5 btn-s rounded-xl"
              >
                <IcLink className="mr-2 text-sm" /> Lier à une facture existante
              </button>
              {/* Livraison répartie sur plusieurs factures → pas de facture
                  unique à relier : le bon passe à Reçu sans facture. */}
              <button
                type="button"
                onClick={doReceivedNoFacture}
                disabled={busy || !allVerified}
                title={!allVerified
                  ? 'Vérifiez d\'abord tous les produits (reçu / manque / non reçu)'
                  : 'Le fournisseur livre en plusieurs factures ? Marquer le bon Reçu sans facture liée'}
                className="px-5 py-2.5 btn-s rounded-xl"
              >
                <IcPackageOpen className="mr-2 text-sm" /> Reçu sans facture
              </button>
            </>
          )}
          {order.status === 'RECU' && order.facture_id && (
            <Link
              to={`/facturation/detail/${order.facture_id}`}
              className="px-5 py-2.5 flex items-center btn-s"
            >
              <IcFileText className="mr-2" /> Voir la facture liée
            </Link>
          )}
          {/* Bon reçu SANS facture liée : rappel + note saisie à la réception. */}
          {order.status === 'RECU' && !order.facture_id && (
            <div className="px-4 py-2.5 rounded-xl bg-gray-100 border-[1.5px] border-gray-500 text-gray-700 text-sm w-fit max-w-full flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-semibold flex items-center shrink-0"><IcPackageOpen className="mr-2 shrink-0" /> Reçu</span>
              {/* Note à DROITE du libellé (choix Ahmed 2026-08-20), pas en dessous,
                  et sans séparateur : juste « Reçu » puis la note. */}
              {order.received_note && (
                <span className="text-gray-600">{order.received_note}</span>
              )}
            </div>
          )}
        </div>
      </div>


      {/* ===== Carte « Lier à une facture existante » — composant partagé
          avec la liste des bons envoyés. ===== */}
      {linkPopOpen && (
        <LinkFacturePopup
          order={order}
          onClose={() => setLinkPopOpen(false)}
          onDone={async () => { setLinkPopOpen(false); await load(); }}
        />
      )}

      {/* ===== Carte « Nouveau produit reçu » : fiche complète sur place
          (mêmes détails que la page Ajouter un produit). Le prix estimé du
          bon est un prix d'achat TTC — HT (fiche) via la TVA société, prix
          de vente précalculé à la marge société. ===== */}
      {createPop && createPortal(
        (() => {
          const achatNum = parseFloat(String(createPop.achat).replace(',', '.'));
          const htNum = Number.isFinite(achatNum) ? achatNum / (1 + (companyTva || 0) / 100) : null;
          const margeNum = parseFloat(createPop.marge);
          const subsFiltered = createPop.category
            ? refs.subs.filter((s) => String(s.category) === String(createPop.category) || String(s.category_id) === String(createPop.category))
            : refs.subs;
          const inputCls = 'w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900';
          return (
            <div className="modal-backdrop z-[85] flex items-center justify-center p-4" onClick={() => setCreatePop(null)}>
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="modal-head py-3.5 items-start gap-3 shrink-0">
                  <div className="flex-1 min-w-0">
                    <h3 className="modal-title leading-tight flex items-center gap-2"><IcCheck size={15} /> Nouveau produit reçu</h3>
                    <p className="text-xs text-gray-300 mt-0.5">Créer sa fiche complète dans la liste des produits</p>
                  </div>
                  <button onClick={() => setCreatePop(null)} className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-white shrink-0" aria-label="Fermer"><IcClose size={13} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {/* Photo + nom / code */}
                  <div className="flex gap-3">
                    <label className="shrink-0 h-24 w-24 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 bg-gray-50 flex items-center justify-center cursor-pointer overflow-hidden" title="Photo du produit">
                      {createPop.imagePreview
                        ? <img src={createPop.imagePreview} alt="" className="h-full w-full object-contain" />
                        : <span className="text-[10px] text-gray-400 font-medium text-center px-1">Photo<br />(optionnel)</span>}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          setCreatePop((c) => ({
                            ...c,
                            imageFile: f || null,
                            imagePreview: f ? URL.createObjectURL(f) : null,
                          }));
                        }}
                      />
                    </label>
                    <div className="flex-1 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Nom du produit *</label>
                        <input
                          type="text"
                          value={createPop.name}
                          onChange={(e) => setCreatePop((c) => ({ ...c, name: e.target.value }))}
                          className={`${inputCls} font-semibold`}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Code-barres</label>
                        <input
                          type="text"
                          value={createPop.code}
                          onChange={(e) => setCreatePop((c) => ({ ...c, code: e.target.value }))}
                          placeholder="Scanner ou saisir…"
                          className={`${inputCls} tabular-nums`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Catégorie / sous-catégorie / laboratoire / stock min */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Catégorie</label>
                      <select
                        value={createPop.category}
                        onChange={(e) => setCreatePop((c) => ({ ...c, category: e.target.value, subcategory: '' }))}
                        className={inputCls}
                      >
                        <option value="">— Aucune —</option>
                        {refs.cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Sous-catégorie</label>
                      <select
                        value={createPop.subcategory}
                        onChange={(e) => setCreatePop((c) => ({ ...c, subcategory: e.target.value }))}
                        className={inputCls}
                      >
                        <option value="">— Aucune —</option>
                        {subsFiltered.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Laboratoire</label>
                      <select
                        value={createPop.laboratoire}
                        onChange={(e) => {
                          // « + Nouveau laboratoire… » dans la liste : ouvre
                          // l'invite de création, la valeur reste inchangée.
                          if (e.target.value === '__new__') { addNewLabo(); return; }
                          setCreatePop((c) => ({ ...c, laboratoire: e.target.value }));
                        }}
                        className={inputCls}
                      >
                        <option value="">— Aucun —</option>
                        <option value="__new__" className="font-semibold text-blue-600">+ Nouveau laboratoire…</option>
                        {refs.labs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Stock minimum</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={createPop.stockMin}
                        onChange={(e) => setCreatePop((c) => ({ ...c, stockMin: e.target.value }))}
                        onFocus={(e) => e.target.select()}
                        className={`${inputCls} tabular-nums`}
                      />
                    </div>
                  </div>

                  {/* Prix : achat TTC (HT affiché) / marge / vente */}
                  <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix d'achat TTC</label>
                        <div className="relative">
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.001"
                            value={createPop.achat}
                            onChange={(e) => setCreateAchat(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className="w-full pl-3 pr-12 py-2.5 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-900 focus:border-gray-900 tabular-nums font-semibold"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                        </div>
                        {htNum != null && (
                          <p className="text-[11px] text-gray-400 mt-1 tabular-nums">
                            HT (fiche) : <span className="font-semibold text-gray-600">{htNum.toFixed(3)}</span> — TVA {companyTva}%
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">Marge</label>
                        <div className="relative">
                          <input
                            type="number"
                            inputMode="decimal"
                            step="0.1"
                            value={createPop.marge}
                            onChange={(e) => setCreateMarge(e.target.value)}
                            onFocus={(e) => e.target.select()}
                            className={`w-full pl-3 pr-9 py-2.5 border border-gray-200 bg-white rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-bold ${Number.isFinite(margeNum) && margeNum < 0 ? 'text-red-600' : 'text-green-700'}`}
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">%</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Prix de vente</label>
                      <div className="relative max-w-[12rem]">
                        <input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.001"
                          value={createPop.vente}
                          onChange={(e) => setCreateVente(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="w-full pl-3 pr-12 py-2.5 border border-green-300 ring-1 ring-green-100 bg-white rounded-xl focus:outline-none focus:border-gray-900 focus:ring-1 focus:ring-gray-900 tabular-nums font-semibold"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">DT</span>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                    <textarea
                      rows="2"
                      value={createPop.description}
                      onChange={(e) => setCreatePop((c) => ({ ...c, description: e.target.value }))}
                      placeholder="Description (optionnel)…"
                      className={`${inputCls} text-sm`}
                    ></textarea>
                  </div>

                  <p className="text-[11px] text-gray-400">
                    La ligne du bon sera reliée au produit créé — le stock viendra de la facture de réception.
                  </p>
                </div>

                <div className="px-4 py-3 border-t border-gray-100 shrink-0 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setCreatePop(null)}
                    className="px-4 py-3 text-sm btn-g rounded-xl transition-all"
                  >
                    Plus tard
                  </button>
                  <button
                    type="button"
                    onClick={saveCreate}
                    disabled={createPop.saving || !(createPop.name || '').trim()}
                    className="px-5 py-3 text-sm btn-p rounded-xl transition-all"
                  >
                    {createPop.saving ? 'Création…' : 'Créer le produit'}
                  </button>
                </div>
              </div>
            </div>
          );
        })(),
        document.body
      )}
    </div>
  );
};

export default CommandeDetail;
